/**
 * The MCOS Clinical dashboard, Dr. Joe's desk.
 *
 * "The only thing that will populate in this dashboard will be critical lab
 * values or other issues that needs to be addressed by me aka the internal
 * clinical team."
 *
 * So it shows exactly three things: critical alerts, the Level 2 review queue,
 * and exceptions on the automated pathway. Nothing routine. An empty dashboard
 * is the correct and expected state, and the empty copy says so, a clinician
 * should not have to wonder whether it is empty or broken.
 *
 * Two design rules taken from the safety spec:
 *
 *   - **Opening an alert does not acknowledge it.** Acknowledging is a button,
 *     and resolving needs a written disposition. That is §8, and it is the
 *     reason this page has no "mark all read".
 *   - **The thresholds are not signed off yet.** While the library reports
 *     PROVISIONAL, the page says so at the top. Presenting unapproved clinical
 *     thresholds as authoritative would be the worst kind of polish.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  StatCard,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { IconAlert, IconCheck, IconHeart, IconLabs, IconSettings } from '../components/icons';

type Severity = 'LEVEL_1' | 'URGENT' | 'LEVEL_2';
type AlertState = 'NEW' | 'NOTIFIED' | 'ACKNOWLEDGED' | 'ACTION_DOCUMENTED' | 'RESOLVED';

interface Alert {
  id: string;
  ruleId: string;
  ruleVersion: string;
  severity: Severity;
  tier: string;
  reason: string;
  analyte: string | null;
  resultValue: number | null;
  resultUnit: string | null;
  uln: number | null;
  labcorpFlag: string | null;
  resultStatus: string | null;
  careModel: string;
  patientState: string | null;
  state: AlertState;
  receivedAt: string;
  acknowledgedAt: string | null;
  escalationCount: number;
  tenant: { id: string; name: string; slug: string };
  patient: { id: string; firstName: string; lastName: string; state: string | null } | null;
}

interface ClinicalException {
  id: string;
  type: string;
  state: string;
  summary: string;
  detail: string | null;
  slaDueAt: string | null;
  escalationCount: number;
  createdAt: string;
  tenant: { name: string; slug: string };
  patient: { id: string; firstName: string; lastName: string; state: string | null } | null;
}

interface LicenceRow {
  id: string;
  state: string;
  licenseNumber: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  status: 'EXPIRED' | 'EXPIRING' | 'OK' | 'NO_EXPIRY';
}

interface ProviderLicenceHealth {
  providerId: string;
  providerName: string;
  isMcosClinical: boolean;
  expired: LicenceRow[];
  expiring: LicenceRow[];
  noExpiry: LicenceRow[];
  okCount: number;
  orderableStates: string[];
  worst: 'EXPIRED' | 'EXPIRING' | 'OK';
}

interface Dashboard {
  critical: Alert[];
  level2: Alert[];
  exceptions: ClinicalException[];
  licences: ProviderLicenceHealth[];
  counts: {
    alerts: Partial<Record<Severity, number>>;
    exceptions: Record<string, number>;
    exceptionsOverdue: number;
    unacknowledgedCritical: number;
    licencesExpired: number;
    licencesExpiring: number;
  };
  governance: { status: string; libraryVersion: string; note: string };
}

const SEV_STYLE: Record<Severity, string> = {
  LEVEL_1: 'bg-red-50 text-red-700 border border-red-200',
  URGENT: 'bg-orange-50 text-orange-700 border border-orange-200',
  LEVEL_2: 'bg-amber-50 text-amber-700 border border-amber-200',
};

const SEV_LABEL: Record<Severity, string> = {
  LEVEL_1: 'Level 1 critical',
  URGENT: 'Urgent',
  LEVEL_2: 'MCOS clinical alert',
};

const EXCEPTION_LABEL: Record<string, string> = {
  MISSING_PATIENT_INFORMATION: 'Missing patient information',
  STATE_COVERAGE_MISMATCH: 'No licensed clinician in that state',
  OUTSIDE_STANDARD_PATHWAY: 'Outside the standard pathway',
  CLINICAL_EXCLUSION: 'Clinical exclusion',
  ORDER_VALIDATION_FAILURE: 'Order validation failed',
  TECHNICAL_FAILURE: 'Technical failure',
  REQUIRES_CLINICIAN_REVIEW: 'Requires clinician review',
  OTHER: 'Other',
};

function when(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  const hours = (Date.now() - d.getTime()) / 3_600_000;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min ago`;
  if (hours < 48) return `${Math.round(hours)} h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ClinicalDashboardPage() {
  const query = useQuery({
    queryKey: ['clinical', 'dashboard'],
    queryFn: () => api.get<Dashboard>('/clinical/dashboard'),
    // A critical result should not wait on a page refresh.
    refetchInterval: 60_000,
  });

  const d = query.data;

  return (
    <div className="space-y-5">
      <PageHeader
        title="MCOS Clinical"
        subtitle="Critical laboratory values and exceptions on the automated pathway. Nothing routine appears here."
      />

      {d && d.governance.status !== 'APPROVED' && (
        <div className="flex items-start gap-2 rounded-sm2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="mt-0.5 shrink-0">
            <IconAlert size={16} />
          </span>
          <p>
            <strong>Thresholds are {d.governance.status.toLowerCase()}.</strong> Alert library
            v{d.governance.libraryVersion} has not been through clinical governance sign-off.
            Level 1 values still need validating against LabCorp's current critical-value policy.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Critical / urgent"
          value={String((d?.counts.alerts.LEVEL_1 ?? 0) + (d?.counts.alerts.URGENT ?? 0))}
          tone={(d?.counts.alerts.LEVEL_1 ?? 0) + (d?.counts.alerts.URGENT ?? 0) > 0 ? 'rose' : 'green'}
          icon={<IconHeart size={16} />}
        />
        <StatCard
          label="Unacknowledged"
          value={String(d?.counts.unacknowledgedCritical ?? 0)}
          tone={(d?.counts.unacknowledgedCritical ?? 0) > 0 ? 'rose' : 'green'}
          icon={<IconAlert size={16} />}
        />
        <StatCard
          label="Clinical alerts"
          value={String(d?.counts.alerts.LEVEL_2 ?? 0)}
          icon={<IconLabs size={16} />}
        />
        <StatCard
          label="Licences expired"
          value={String(d?.counts.licencesExpired ?? 0)}
          deltaLabel={
            (d?.counts.licencesExpiring ?? 0) > 0
              ? `${d?.counts.licencesExpiring} expiring soon`
              : undefined
          }
          tone={(d?.counts.licencesExpired ?? 0) > 0 ? 'rose' : 'green'}
          icon={<IconSettings size={16} />}
        />
      </div>

      {query.isLoading ? (
        <Card>
          <LoadingRows rows={5} />
        </Card>
      ) : query.isError ? (
        <Card>
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        </Card>
      ) : (
        <>
          <AlertSection
            title="Critical and urgent"
            subtitle="Immediate escalation. The report is held from the patient until these are dealt with."
            alerts={d?.critical ?? []}
            emptyBody="No critical results outstanding. This is the expected state."
          />
          <AlertSection
            title="MCOS clinical alerts"
            subtitle="Timely review. These do not hold the patient's report."
            alerts={d?.level2 ?? []}
            emptyBody="Nothing awaiting review."
          />
          <LicenceSection licences={d?.licences ?? []} />
          <ExceptionSection exceptions={d?.exceptions ?? []} />
        </>
      )}
    </div>
  );
}

function AlertSection({
  title,
  subtitle,
  alerts,
  emptyBody,
}: {
  title: string;
  subtitle: string;
  alerts: Alert[];
  emptyBody: string;
}) {
  return (
    <Card title={title} subtitle={subtitle} padded={false}>
      {alerts.length === 0 ? (
        <div className="p-5">
          <EmptyState title="All clear" body={emptyBody} />
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Patient</Th>
              <Th>Finding</Th>
              <Th>Severity</Th>
              <Th>Received</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <AlertRow key={a.id} alert={a} />
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [disposition, setDisposition] = useState('');

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['clinical', 'dashboard'] });
  };

  const ack = useMutation({
    mutationFn: () => api.post(`/clinical/alerts/${alert.id}/acknowledge`, {}),
    onSuccess: invalidate,
  });
  const resolve = useMutation({
    mutationFn: () => api.post(`/clinical/alerts/${alert.id}/resolve`, { disposition }),
    onSuccess: () => {
      setDisposition('');
      setOpen(false);
      invalidate();
    },
  });

  return (
    <>
      <tr className={open ? 'bg-ink/5' : undefined}>
        <Td>
          <div className="font-medium text-ink">
            {alert.patient ? `${alert.patient.firstName} ${alert.patient.lastName}` : '-'}
          </div>
          <div className="text-xs text-ink-muted">
            {alert.tenant.name}
            {alert.patientState ? ` · ${alert.patientState}` : ''}
            {alert.careModel === 'NURSE_MCOS_CLINICAL' ? ' · nurse-enrolled' : ''}
          </div>
        </Td>
        <Td>
          <div className="text-ink">{alert.reason}</div>
          <div className="mt-0.5 text-xs text-ink-muted">
            {alert.analyte ? `${alert.analyte} ` : ''}
            {alert.resultValue !== null ? `${alert.resultValue} ${alert.resultUnit ?? ''}` : ''}
            {alert.labcorpFlag ? ' · flagged critical by LabCorp' : ''}
            {alert.resultStatus === 'PARTIAL' ? ' · partial result' : ''}
          </div>
        </Td>
        <Td>
          <Pill className={SEV_STYLE[alert.severity]}>{SEV_LABEL[alert.severity]}</Pill>
          <div className="mt-1 text-[11px] text-ink-muted">
            {alert.acknowledgedAt ? 'acknowledged' : alert.state.toLowerCase()}
            {alert.escalationCount > 1 ? ` · escalated ${alert.escalationCount}×` : ''}
          </div>
        </Td>
        <Td>{when(alert.receivedAt)}</Td>
        <Td align="right">
          <div className="flex justify-end gap-1.5">
            {!alert.acknowledgedAt && (
              <Button size="sm" loading={ack.isPending} onClick={() => ack.mutate()}>
                Acknowledge
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setOpen(!open)}>
              {open ? 'Close' : 'Detail'}
            </Button>
          </div>
        </Td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-ink/5 px-5 py-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-1 text-xs text-ink-muted">
                <div>
                  Rule <span className="font-mono">{alert.ruleId}</span> · library v
                  {alert.ruleVersion} · {alert.tier.toLowerCase().replace(/_/g, ' ')}
                </div>
                {alert.uln !== null && alert.uln !== undefined && (
                  <div>Laboratory upper limit of normal: {alert.uln}</div>
                )}
                <div>Received {new Date(alert.receivedAt).toLocaleString()}</div>
                {alert.acknowledgedAt && (
                  <div>Acknowledged {new Date(alert.acknowledgedAt).toLocaleString()}</div>
                )}
              </div>

              <div>
                <Field
                  label="Disposition"
                  hint="Required to resolve. Recorded in the audit trail."
                >
                  <textarea
                    rows={3}
                    value={disposition}
                    onChange={(e) => setDisposition(e.target.value)}
                    className={inputClass}
                    placeholder="Patient contacted; repeat draw ordered; PCP notified."
                  />
                </Field>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    loading={resolve.isPending}
                    disabled={!alert.acknowledgedAt || disposition.trim().length < 3}
                    title={
                      !alert.acknowledgedAt
                        ? 'Acknowledge the alert first'
                        : disposition.trim().length < 3
                          ? 'A disposition is required'
                          : undefined
                    }
                    onClick={() => resolve.mutate()}
                  >
                    Resolve
                  </Button>
                  {resolve.error && (
                    <span className="text-xs text-red-600">
                      {resolve.error instanceof Error ? resolve.error.message : 'Could not resolve.'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ExceptionSection({ exceptions }: { exceptions: ClinicalException[] }) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['clinical', 'dashboard'] });
  };
  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/clinical/exceptions/${id}/resolve`, { resolution }),
    onSuccess: () => {
      setResolution('');
      setOpenId(null);
      invalidate();
    },
  });

  return (
    <Card
      title="Exceptions"
      subtitle="Cases the automated pathway could not complete. These never expire, they keep escalating until handled."
      padded={false}
    >
      {exceptions.length === 0 ? (
        <div className="p-5">
          <EmptyState
            title="Nothing stuck"
            body="Every enrolment is flowing through the automated pathway."
          />
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Patient</Th>
              <Th>Problem</Th>
              <Th>SLA</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {exceptions.map((e) => {
              const overdue = e.slaDueAt !== null && new Date(e.slaDueAt).getTime() <= Date.now();
              const isOpen = openId === e.id;
              return (
                <>
                  <tr key={e.id} className={isOpen ? 'bg-ink/5' : undefined}>
                    <Td>
                      <div className="font-medium text-ink">
                        {e.patient ? `${e.patient.firstName} ${e.patient.lastName}` : '-'}
                      </div>
                      <div className="text-xs text-ink-muted">
                        {e.tenant.name}
                        {e.patient?.state ? ` · ${e.patient.state}` : ''}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-ink">
                        {EXCEPTION_LABEL[e.type] ?? e.type}
                      </div>
                      <div className="text-xs text-ink-muted">{e.summary}</div>
                    </Td>
                    <Td>
                      {overdue ? (
                        <Pill className="bg-red-50 text-red-700 border border-red-200">
                          past SLA
                        </Pill>
                      ) : (
                        <span className="text-ink">{when(e.slaDueAt)}</span>
                      )}
                      {e.escalationCount > 0 && (
                        <div className="mt-1 text-[11px] text-ink-muted">
                          escalated {e.escalationCount}×
                        </div>
                      )}
                    </Td>
                    <Td align="right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setOpenId(isOpen ? null : e.id)}
                      >
                        {isOpen ? 'Close' : 'Handle'}
                      </Button>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr key={`${e.id}-detail`}>
                      <td colSpan={4} className="bg-ink/5 px-5 py-4">
                        {e.detail && (
                          <p className="mb-3 max-w-2xl whitespace-pre-line text-sm text-ink">
                            {e.detail}
                          </p>
                        )}
                        <div className="max-w-xl">
                          <Field label="Resolution" hint="What was done. Recorded in the audit trail.">
                            <textarea
                              rows={2}
                              value={resolution}
                              onChange={(ev) => setResolution(ev.target.value)}
                              className={inputClass}
                              placeholder="Licence for that state added; monitoring plan re-run."
                            />
                          </Field>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            size="sm"
                            loading={resolve.isPending}
                            disabled={resolution.trim().length < 3}
                            onClick={() => resolve.mutate(e.id)}
                          >
                            Resolve
                          </Button>
                          <span className="text-[11px] text-ink-muted">
                            Resolving records the decision. It does not by itself unblock an order -
                            the underlying fact has to change.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Licence expiry
 *
 * On this dashboard because a lapsed licence does not fail loudly: it silently
 * removes a state from the ordering pool, and the patient there gets a coverage
 * exception that reads like a configuration fault rather than an administrative
 * one. Eleven of Dr. Joe's licences share a single expiry date, so the warning
 * has to arrive well before that morning.
 * ------------------------------------------------------------------ */

const LIC_STYLE: Record<LicenceRow['status'], string> = {
  EXPIRED: 'bg-red-50 text-red-700 border border-red-200',
  EXPIRING: 'bg-amber-50 text-amber-700 border border-amber-200',
  OK: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  NO_EXPIRY: 'bg-ink/5 text-ink-muted border border-ink/10',
};

function LicenceSection({ licences }: { licences: ProviderLicenceHealth[] }) {
  const qc = useQueryClient();
  const check = useMutation({
    mutationFn: () => api.post('/clinical/licences/check', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clinical', 'dashboard'] });
    },
  });

  const anyProblem = licences.some((l) => l.worst !== 'OK');

  return (
    <Card
      title="State licences"
      subtitle="A lapsed licence stops MCOS ordering in that state. Renew before the date, not after."
      action={
        <Button size="sm" variant="secondary" loading={check.isPending} onClick={() => check.mutate()}>
          Re-check
        </Button>
      }
    >
      {licences.length === 0 ? (
        <EmptyState
          title="No licences on file"
          body="Add states of licensure to an MCOS Clinical provider and they will be tracked here."
        />
      ) : (
        <div className="space-y-5">
          {licences.map((l) => (
            <div key={l.providerId}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium text-ink">{l.providerName}</span>
                <Pill
                  className={
                    l.worst === 'EXPIRED'
                      ? LIC_STYLE.EXPIRED
                      : l.worst === 'EXPIRING'
                        ? LIC_STYLE.EXPIRING
                        : LIC_STYLE.OK
                  }
                >
                  {l.worst === 'EXPIRED'
                    ? `${l.expired.length} expired`
                    : l.worst === 'EXPIRING'
                      ? `${l.expiring.length} expiring`
                      : 'all current'}
                </Pill>
                <span className="text-xs text-ink-muted">
                  can order in {l.orderableStates.length} jurisdiction
                  {l.orderableStates.length === 1 ? '' : 's'}
                </span>
              </div>

              {l.expired.length > 0 && (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700">
                    Expired, cannot order here
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {l.expired.map((r) => (
                      <Pill key={r.id} className={LIC_STYLE.EXPIRED}>
                        {r.state} · {r.expiresAt?.slice(0, 10)}
                      </Pill>
                    ))}
                  </div>
                </div>
              )}

              {l.expiring.length > 0 && (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                    Expiring soon
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {l.expiring.map((r) => (
                      <Pill key={r.id} className={LIC_STYLE.EXPIRING}>
                        {r.state} · {r.daysRemaining}d
                      </Pill>
                    ))}
                  </div>
                </div>
              )}

              {l.noExpiry.length > 0 && (
                <p className="mt-2 text-[11px] text-ink-muted">
                  No expiry date on file, treated as non-expiring:{' '}
                  {l.noExpiry.map((r) => r.state).join(', ')}
                </p>
              )}

              {l.worst === 'OK' && l.okCount > 0 && (
                <p className="mt-1 text-xs text-ink-muted">
                  {l.okCount} licence{l.okCount === 1 ? '' : 's'} current, none within the warning
                  window.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {anyProblem && (
        <p className="mt-4 border-t border-ink/5 pt-3 text-xs text-ink-muted">
          Renewing a licence clears its alert automatically on the next check, nothing needs
          dismissing by hand.
        </p>
      )}
      {check.error && (
        <p className="mt-2 text-xs text-red-600">
          {check.error instanceof Error ? check.error.message : 'Could not run the check.'}
        </p>
      )}
    </Card>
  );
}
